// Opt-in upload diagnostics. Off unless `localStorage.uploadDebug = 'true'` (or setUploadDebugEnabled(true)),
// so nothing is logged in production; it exists because the upload path spans browser, api and object storage
// and the failure modes (cors, presign, resume, part retries) are otherwise invisible from the outside.
const LOG_PREFIX = '[Upload]'
const STORAGE_KEY = 'uploadDebug'
// S3 requires every part except the last to be at least 5 MiB and caps a multipart upload at 10000 parts
const MIN_MULTIPART_PART_SIZE_IN_BYTES = 5 * 1024 * 1024
const MAX_MULTIPART_PART_COUNT = 10000

let nextUploadDebugId = 1

function readStoredPreference(): boolean|null {
  try {
    const storedValue = window.localStorage?.getItem(STORAGE_KEY)
    if(storedValue === 'true') {
      return true
    }
    if(storedValue === 'false') {
      return false
    }
    return null
  } catch {
    return null
  }
}

let isEnabled = readStoredPreference() ?? false

export function setUploadDebugEnabled(value: boolean): void {
  isEnabled = value
  try {
    window.localStorage?.setItem(STORAGE_KEY, String(value))
  } catch {
    // storage is unavailable in some embedded webviews, the in-memory flag still works
  }
}

export function formatBytes(bytes: number): string {
  if(!Number.isFinite(bytes)) {
    return String(bytes)
  }
  if(bytes < 1024) {
    return `${bytes}B`
  }
  if(bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)}KB`
  }
  if(bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`
}

export function formatThroughput(bytes: number, durationInMs: number): string {
  if(durationInMs <= 0) {
    return 'n/a'
  }
  const megabytesPerSecond = (bytes / (1024 * 1024)) / (durationInMs / 1000)
  return `${megabytesPerSecond.toFixed(2)}MB/s`
}

export interface IUploadDebugSession {
  readonly id: string
  log(event: string, data?: Record<string, unknown>): void
  warn(event: string, data?: Record<string, unknown>): void
  elapsedInMs(): number
}

export function createUploadDebugSession(scope: string): IUploadDebugSession {
  const id = `u${nextUploadDebugId++}`
  const startedAt = performance.now()
  const label = `${LOG_PREFIX}[${id}][${scope}]`
  return {
    id,
    log(event: string, data?: Record<string, unknown>): void {
      if(!isEnabled) {
        return
      }
      // eslint-disable-next-line no-console
      console.log(`${label} ${event}`, data ?? '')
    },
    warn(event: string, data?: Record<string, unknown>): void {
      if(!isEnabled) {
        return
      }
      // eslint-disable-next-line no-console
      console.warn(`${label} ⚠ ${event}`, data ?? '')
    },
    elapsedInMs(): number {
      return Math.round(performance.now() - startedAt)
    },
  }
}

export function reportUploadInputProblems(
  debugSession: IUploadDebugSession,
  file: File,
  targetType: string,
  target: unknown,
): void {
  if(!file.size) {
    debugSession.warn('file has no bytes, the upload will produce an empty object', {name: file.name})
  }
  if(!file.type) {
    debugSession.warn('file has no mime type, the server will fall back to application/octet-stream', {name: file.name})
  }
  const targetRecord = (target ?? {}) as Record<string, unknown>
  const emptyTargetKeys = Object.keys(targetRecord).filter(key => {
    return targetRecord[key] === undefined || targetRecord[key] === ''
  })
  if(emptyTargetKeys.length) {
    debugSession.warn('upload target has empty fields, the server will reject the session', {
      targetType,
      emptyTargetKeys,
    })
  }
}

export function reportUploadSessionPlanProblems(
  debugSession: IUploadDebugSession,
  plan: {partSize: number; partCount: number; expiresAt: Date|null; fileSize: number; signedPartCount: number},
): void {
  const plannedBytes = plan.partSize * plan.partCount
  if(plannedBytes < plan.fileSize) {
    debugSession.warn('part plan cannot cover the file', {
      plannedBytes: formatBytes(plannedBytes),
      fileSize: formatBytes(plan.fileSize),
    })
  }
  if(plan.partCount > 1 && plan.partSize < MIN_MULTIPART_PART_SIZE_IN_BYTES) {
    debugSession.warn('part size is below the 5MiB multipart minimum, storage will reject the parts', {
      partSize: formatBytes(plan.partSize),
    })
  }
  if(plan.partCount > MAX_MULTIPART_PART_COUNT) {
    debugSession.warn('part count exceeds the 10000 multipart maximum', {partCount: plan.partCount})
  }
  if(!plan.signedPartCount) {
    debugSession.warn('session returned no presigned parts, every part will need a separate sign request')
  }
  if(!plan.expiresAt) {
    return
  }
  const millisecondsUntilExpiry = plan.expiresAt.getTime() - Date.now()
  if(millisecondsUntilExpiry <= 0) {
    debugSession.warn('session is already expired', {expiresAt: plan.expiresAt})
    return
  }
  debugSession.log('session expires in', {minutes: Math.round(millisecondsUntilExpiry / 60000)})
}
